import { mt, MBtn, MCard, elapsedMin } from "./shared";

const SUP_ROLES = ["supervisor", "watch_commander", "records_supervisor", "admin"];
const DISP_ROLES = ["police_dispatcher", "fire_dispatcher", "ems_dispatcher", "call_taker"];
const COMMAND_ROLES = ["watch_commander", "admin"];

function Stat({ label, value, color = mt.text, testid }) {
  return (
    <div data-testid={testid} className="rounded-lg p-3 flex flex-col gap-1"
      style={{ background: mt.surface, border: `1px solid ${mt.border}` }}>
      <span className="text-[10px] font-mono uppercase" style={{ color: mt.dim }}>{label}</span>
      <span className="text-xl font-head font-bold" style={{ color }}>{value}</span>
    </div>
  );
}

export function MobileHome({ home, user, onStatus, onTab, onEmergency }) {
  if (!home) return <div className="p-6 font-mono text-sm" style={{ color: mt.dim }}>Loading home…</div>;
  const unit = home.unit;
  const inc = home.active_incident;
  const isSup = SUP_ROLES.includes(user.role) || DISP_ROLES.includes(user.role);
  const isCmd = COMMAND_ROLES.includes(user.role);
  return (
    <div data-testid="mobile-home" className="flex flex-col gap-3 p-3 pb-28">
      {/* Identity + unit status */}
      <MCard testid="mobile-home-identity">
        <div className="flex items-center justify-between">
          <div>
            <div className="font-head font-bold text-base" style={{ color: mt.text }}>{user.name}</div>
            <div className="text-[11px] font-mono uppercase" style={{ color: mt.dim }}>
              {user.role.replace(/_/g, " ")} {user.badge ? `· UNIT ${user.badge}` : ""}
            </div>
          </div>
          {unit && (
            <span data-testid="mobile-home-status" className="px-3 py-1.5 rounded-full text-xs font-head font-bold"
              style={{ background: unit.status === "available" ? mt.green : mt.amber, color: "#fff" }}>
              {(unit.status || "").replace(/_/g, " ").toUpperCase()}
            </span>
          )}
        </div>
      </MCard>

      {home.panic_active > 0 && (
        <MCard testid="mobile-home-panic" style={{ border: `2px solid ${mt.red}` }}>
          <span className="font-head font-bold text-sm" style={{ color: mt.red }}>
            🚨 {home.panic_active} ACTIVE EMERGENCY ACTIVATION{home.panic_active > 1 ? "S" : ""}
          </span>
        </MCard>
      )}

      {/* Current incident */}
      {inc ? (
        <MCard testid="mobile-home-incident" style={{ border: `1.5px solid ${inc.priority === 1 ? mt.red : mt.border}` }}>
          <div className="flex items-center justify-between mb-1">
            <span className="font-head font-bold text-sm" style={{ color: mt.text }}>
              P{inc.priority} · {inc.call_type_label}
            </span>
            <span className="text-[10px] font-mono" style={{ color: mt.dim }}>{elapsedMin(inc.created_at)} min</span>
          </div>
          <div className="text-xs font-mono mb-1" style={{ color: mt.dim }}>
            {inc.cc_number} · {(inc.location || {}).address}{(inc.location || {}).apt ? ` #${inc.location.apt}` : ""}
          </div>
          {inc.premise_summary && (
            <div className="text-[11px] font-mono mb-2 py-1 px-2 rounded" style={{ color: "#FCA5A5", background: "#3b0d0d" }}>
              {inc.premise_summary}
            </div>
          )}
          <div className="grid grid-cols-3 gap-2 mt-2">
            <MBtn small color={mt.cyan} onClick={() => onStatus("acknowledged")} testid="mobile-home-ack">ACK</MBtn>
            <MBtn small color={mt.blue} onClick={() => onStatus("enroute")} testid="mobile-home-enroute">EN ROUTE</MBtn>
            <MBtn small color={mt.green} onClick={() => onStatus("onscene")} testid="mobile-home-onscene">ON SCENE</MBtn>
          </div>
        </MCard>
      ) : (
        <MCard testid="mobile-home-noincident">
          <span className="text-sm font-mono" style={{ color: mt.dim }}>No active assignment.</span>
        </MCard>
      )}

      {/* Quick actions */}
      <div className="grid grid-cols-2 gap-2">
        <MBtn full color={mt.red} onClick={onEmergency} testid="mobile-home-emergency">🚨 EMERGENCY</MBtn>
        <MBtn full outline color={mt.amber} onClick={() => onTab("alerts")} testid="mobile-home-alerts">
          ALERTS {home.unacked_alerts > 0 ? `(${home.unacked_alerts} UNACKED)` : ""}
        </MBtn>
        <MBtn full outline color={mt.blue} onClick={() => onTab("map")} testid="mobile-home-map">OPEN MAP</MBtn>
        <MBtn full outline color={mt.cyan} onClick={() => onTab("calls")} testid="mobile-home-calls">CALLS</MBtn>
      </div>

      {/* Supervisor / dispatcher overview */}
      {isSup && (
        <>
          <div className="text-[11px] font-head font-bold uppercase mt-1" style={{ color: mt.dim }}>
            {isCmd ? "Command Overview" : "Supervisor Overview"}
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Stat label="Priority 1 Active" value={home.p1_active ?? 0} color={home.p1_active ? mt.red : mt.text} testid="mobile-stat-p1" />
            <Stat label="Priority 2 Active" value={home.p2_active ?? 0} color={home.p2_active ? mt.amber : mt.text} testid="mobile-stat-p2" />
            <Stat label="Calls Waiting" value={home.pending_calls ?? 0} testid="mobile-stat-pending" />
            <Stat label="Needs Re-Assign" value={home.needs_reassignment ?? 0} color={home.needs_reassignment ? mt.amber : mt.text} testid="mobile-stat-reassign" />
            <Stat label="Units Available" value={(home.units_by_status || {}).available ?? 0} color={mt.green} testid="mobile-stat-avail" />
            <Stat label="Emergency Activations" value={home.active_emergencies ?? 0} color={home.active_emergencies ? mt.red : mt.text} testid="mobile-stat-emg" />
          </div>
          {home.longest_waiting && (
            <MCard testid="mobile-home-longest">
              <span className="text-[10px] font-mono uppercase" style={{ color: mt.dim }}>Longest waiting call</span>
              <div className="text-xs font-mono mt-1" style={{ color: mt.text }}>
                {home.longest_waiting.cc_number} · {home.longest_waiting.call_type_label} · waiting {elapsedMin(home.longest_waiting.created_at)} min
              </div>
            </MCard>
          )}
        </>
      )}
    </div>
  );
}
